import { isFundType } from '@modules/consolidator/utils/asset-type-detection/asset-type-detection'

const CNPJ_MASCARADO = /^\d{2}\.\d{3}\.\d{3}\/\d{4}-\d{2}$/
const CNPJ_SEM_MASCARA = /^\d{14}$/
const SEPARADOR_SUBCLASSE = '_'

const trimOrEmpty = (value) => String(value || '').trim()

const aplicarMascaraCnpj = (digitos) =>
  `${digitos.slice(0, 2)}.${digitos.slice(2, 5)}.${digitos.slice(5, 8)}` +
  `/${digitos.slice(8, 12)}-${digitos.slice(12)}`

const asCnpj = (value) => {
  const texto = trimOrEmpty(value)
  if (CNPJ_MASCARADO.test(texto)) return texto
  if (CNPJ_SEM_MASCARA.test(texto)) return aplicarMascaraCnpj(texto)
  return ''
}

export const referenciaTemCnpj = (value) =>
  Boolean(splitReferenciaDeFundo({ asset: { referencia: value } }).cnpj)

const splitReferenciaDeFundo = ({ asset } = {}) => {
  const referencia = trimOrEmpty(asset && asset.referencia)
  if (!referencia) return { cnpj: '', subclasse: '' }
  const separador = referencia.indexOf(SEPARADOR_SUBCLASSE)
  if (separador === -1) return { cnpj: asCnpj(referencia), subclasse: '' }
  const cnpj = asCnpj(referencia.slice(0, separador))
  if (!cnpj) return { cnpj: '', subclasse: '' }
  return { cnpj, subclasse: referencia.slice(separador + 1).trim() }
}

const getFundCnpjSources = ({ asset } = {}) => {
  if (!asset) return []
  return [asset.cnpj_fundo, asset.fundo_cnpj, asset.cnpj]
    .map(trimOrEmpty)
    .filter(Boolean)
}

const primeiroCnpjDasColunas = ({ asset } = {}) =>
  getFundCnpjSources({ asset }).map(asCnpj).find(Boolean) || ''

const resolveCascaCnpj = ({ asset } = {}) => {
  if (!asset) return ''
  return trimOrEmpty(asset.cnpj_fundo) || trimOrEmpty(asset.fundo_cnpj)
}

export const getFundSearchableIdentifiers = ({ asset } = {}) => {
  if (!asset) return []
  const sources = getFundCnpjSources({ asset })
  const referencia = trimOrEmpty(asset.referencia)
  return referencia ? [...sources, referencia] : sources
}

export const resolveFundIdentifiers = ({ asset } = {}) => {
  if (!asset) return { cnpj: '', subclasse: '' }
  const daReferencia = splitReferenciaDeFundo({ asset })
  const daColuna = primeiroCnpjDasColunas({ asset })
  const cnpj = daColuna || daReferencia.cnpj
  const subclasseDaColuna = resolveFundSubclasse({ asset })
  const referenciaDescreveOMesmoFundo = cnpj === daReferencia.cnpj
  return {
    cnpj,
    subclasse:
      subclasseDaColuna ||
      (referenciaDescreveOMesmoFundo ? daReferencia.subclasse : ''),
  }
}

export const resolveFundSubclasse = ({ asset } = {}) => {
  if (!asset) return ''
  return trimOrEmpty(asset.cnpj_subclasse) || trimOrEmpty(asset.fundo_subclasse)
}

export const resolveFundCnpjFallback = ({ asset } = {}) => {
  if (!asset) return ''
  const daColuna = [asset.fundo_cnpj, asset.cnpj_fundo, asset.cnpj]
    .map(asCnpj)
    .find(Boolean)
  return daColuna || splitReferenciaDeFundo({ asset }).cnpj
}

export const buildFundLabelReference = ({ asset, cnpjOnly = false } = {}) => {
  if (!asset) return ''
  const subclasse = resolveFundSubclasse({ asset })
  const cascaCnpj = resolveCascaCnpj({ asset })
  const cnpjMaster = trimOrEmpty(asset.cnpj)
  if (subclasse && !cnpjOnly) {
    const cnpjForCasca = cascaCnpj || cnpjMaster
    return cnpjForCasca ? `${cnpjForCasca}_${subclasse}` : ''
  }
  if (cascaCnpj && cascaCnpj !== cnpjMaster) return cascaCnpj
  return cnpjMaster || cascaCnpj
}

export const buildFundFullName = ({ asset, assetType } = {}) => {
  if (!asset) return ''
  const name = asset.nome || ''
  if (assetType !== undefined && !isFundType(assetType)) return name
  const reference = buildFundLabelReference({ asset })
  return reference ? `(${reference}) ${name}` : name
}
